import { useMemo, useState } from 'react';
import { useBudget } from '../store/BudgetContext';
import { useToast } from '../components/Toast';
import { api } from '../api/client';
import { ApiError } from '../api/types';
import { theme } from '../theme';
import { Icon, categoryIconName } from '../components/Icon';
import { CategoryPlanPad } from '../components/CategoryPlanPad';
import { fmtK, fmtMoney } from '../utils/format';
import { computeMonthBudget } from '../utils/month-budget';
import { oneOffIdsFrom } from '../utils/oneOff';
import { reserveSets } from '../utils/reserveState';
import { newClientId } from '../utils/ids';

const MONTHS_GEN = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];

/** Вкладка «Бюджет» (Антверпен 2.0 §2): pace-gauge, водопад тинтов, конверты. */
export function Budget() {
  const { viewData, refresh, updateTx, putReserve, sweepReserve } = useBudget();
  const toast = useToast();
  const now = new Date();
  const monthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const oneOffIds = useMemo(
    () => oneOffIdsFrom(viewData?.operations ?? []),
    [viewData],
  );
  const { released, swept } = useMemo(
    () => reserveSets(viewData, monthKey),
    [viewData, monthKey],
  );

  const [editTarget, setEditTarget] = useState(false);
  const [targetStr, setTargetStr] = useState('');
  const [busy, setBusy] = useState(false);
  const [sweepCat, setSweepCat] = useState<string | null>(null);
  const [planCat, setPlanCat] = useState<string | null>(null);

  const mb = useMemo(
    () => (viewData ? computeMonthBudget(viewData.operations, viewData.categories, viewData.settings, new Date(), oneOffIds, released) : null),
    [viewData, oneOffIds, released],
  );

  // резерв по нерегулярным категориям: max(0, план − потрачено в этом месяце)
  const reserveRows = useMemo(() => {
    if (!viewData) return [];
    const spentBy = new Map<string, number>();
    for (const op of viewData.operations) {
      if (!op.date.startsWith(monthKey)) continue;
      const cat = viewData.categories.find((c) => c.name === op.category);
      if (cat && cat.type === 'Расход' && !cat.is_daily) {
        spentBy.set(cat.name, (spentBy.get(cat.name) || 0) + op.amount);
      }
    }
    return viewData.categories
      .filter((c) => c.type === 'Расход' && !c.is_daily && (c.plan_month || 0) > 0)
      .map((c) => ({ name: c.name, reserve: Math.max(0, (c.plan_month || 0) - (spentBy.get(c.name) || 0)) }))
      .filter((r) => r.reserve > 0 && !swept.has(r.name))
      .sort((a, b) => b.reserve - a.reserve);
  }, [viewData, monthKey, swept]);

  // крупные повседневные траты — кандидаты в «разовое»
  const bigDaily = useMemo(() => {
    if (!viewData || !mb) return [];
    const catByName = new Map(viewData.categories.map((c) => [c.name, c] as const));
    const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    const threshold = Math.max(1500, mb.availableFlexTotal / daysInMonth);
    return viewData.operations
      .filter((op) => {
        if (!op.date.startsWith(monthKey)) return false;
        const cat = catByName.get(op.category);
        const type = cat?.type ?? op.type;
        const daily = cat ? (cat.type === 'Расход' && !!cat.is_daily) : type === 'Расход';
        return daily && (op.amount >= threshold || oneOffIds.has(op.id));
      })
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 6);
  }, [viewData, mb, oneOffIds, monthKey]);

  const openGoals = useMemo(() => (viewData?.goals ?? []).filter((g) => g.remaining > 0), [viewData]);

  if (!viewData || !mb) return null;
  const currency = viewData.settings.currency ?? '₽';
  const goalsMonthly = viewData.goals.reduce((s, g) => s + (g.monthly_plan || 0), 0);

  const heldReserve = reserveRows.filter((r) => !released.has(r.name)).reduce((s, r) => s + r.reserve, 0);
  const freedReserve = reserveRows.filter((r) => released.has(r.name)).reduce((s, r) => s + r.reserve, 0);

  // пейс
  const tol = Math.max(2000, mb.availableFlexTotal * 0.05);
  const pace: 'ahead' | 'on' | 'over' = mb.paceDelta > tol ? 'ahead' : mb.paceDelta < -tol ? 'over' : 'on';
  const paceColor = pace === 'ahead' ? theme.colors.green : pace === 'over' ? theme.colors.accent : theme.colors.sub;
  const weekColor = mb.availableFlexTotal <= 0 ? theme.colors.accent : pace === 'over' ? theme.colors.accent : theme.colors.ink;

  // геометрия pace-gauge: всё в долях месячного флекс-пула
  const gauge = (() => {
    const total = Math.max(1, mb.availableFlexTotal);
    const pct = (v: number) => Math.max(0, Math.min(100, (v / total) * 100));
    return {
      fill: pct(mb.flexSpent),
      mark: pct(mb.expectedFlexByNow),
      tolLeft: pct(mb.expectedFlexByNow - tol),
      tolWidth: pct(mb.expectedFlexByNow + tol) - pct(mb.expectedFlexByNow - tol),
    };
  })();

  // водопад: доли от ожидаемого дохода
  const wf = (() => {
    const inc = Math.max(1, mb.expectedIncome);
    const fixedPct = (mb.fixedExpected / inc) * 100;
    const goalPct = (mb.monthTarget / inc) * 100;
    const flexPct = Math.max(0, 100 - fixedPct - goalPct);
    return { fixedPct, goalPct, flexPct };
  })();

  async function saveTarget(v: number | null) {
    setBusy(true);
    try {
      await api.setSetting('month_target', v);
      await refresh();
      toast.show(v ? `Цель месяца: ${fmtMoney(v, currency)}` : 'Цель месяца снята');
      setEditTarget(false);
    } catch (e) {
      toast.show(e instanceof ApiError ? e.message : 'Ошибка', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function sweep(catName: string, reserve: number, goalName: string) {
    setBusy(true);
    try {
      // транзакционно на сервере (state + взнос + have); идемпотентно по id —
      // двойной sweep с двух устройств больше не задваивает деньги
      const r = await sweepReserve({
        id: newClientId(),
        month: monthKey,
        category: catName,
        goal: goalName,
        amount: reserve,
      });
      setSweepCat(null);
      toast.reward({
        title: catName,
        amount: `${fmtK(reserve)} → «${goalName}»`,
        coin: false,
        sub: `конверт погашен — смещён в цель${r.synced ? '' : ' · синхронизируется позже'}`,
      });
    } catch (e) {
      toast.show(e instanceof ApiError ? e.message : 'Ошибка', 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ padding: '26px 22px 100px' }}>
      <span className="caption">На что живём</span>
      <h1 style={{ margin: '4px 0 4px', fontSize: 30, fontWeight: 400 }}>Бюджет</h1>
      <div className="caption" style={{ marginBottom: 22 }}>{MONTHS_GEN[now.getMonth()]} {now.getFullYear()}</div>

      {mb.availableFlexTotal <= 0 ? (
        <div style={{ fontSize: 15, color: theme.colors.accent, lineHeight: 1.5 }}>
          Доход не покрывает обязательное + цель. Снизь цель месяца или планы нерегулярных категорий.
        </div>
      ) : (
        <>
          {/* 01 · Pace-gauge */}
          <SectionHead n="01" title="На эту неделю" />
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, marginTop: 10 }}>
            <span className="num" style={{ fontSize: 44, lineHeight: 1, color: weekColor }}>{fmtMoney(Math.round(mb.weekSafeToSpend), currency)}</span>
            <span style={{ fontSize: 13, color: theme.colors.sub, textAlign: 'right', lineHeight: 1.4 }}>
              ≈ {fmtMoney(mb.dynamicDailyLimit, currency)}/день<br />
              {mb.daysLeftThisWeek} {plural(mb.daysLeftThisWeek, 'день', 'дня', 'дней')} до конца недели
            </span>
          </div>

          <div style={{ position: 'relative', height: 12, background: theme.colors.line, margin: '20px 0 0' }}>
            <div
              style={{
                position: 'absolute',
                left: `${gauge.tolLeft}%`,
                top: 0,
                bottom: 0,
                width: `${gauge.tolWidth}%`,
                background: 'repeating-linear-gradient(45deg, rgba(20,19,15,.22) 0 1px, transparent 1px 4px)',
              }}
            />
            <div className="bar" style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${gauge.fill}%`, background: theme.colors.ink }} />
            <div style={{ position: 'absolute', left: `${gauge.mark}%`, top: -6, bottom: -6, width: 2, background: theme.colors.ink }} />
            <div style={{ position: 'absolute', left: `${gauge.mark}%`, top: -6, width: 8, height: 2, background: theme.colors.ink }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: theme.colors.sub, marginTop: 10 }}>
            <span>потрачено <span style={{ fontVariantNumeric: 'tabular-nums', color: theme.colors.ink }}>{fmtK(mb.flexSpent)}</span></span>
            <span>к {now.getDate()}-му по плану <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtK(mb.expectedFlexByNow)} ±{fmtK(tol)}</span></span>
          </div>
          <div style={{ fontSize: 13, marginTop: 8, color: paceColor }}>
            {pace === 'over' ? 'выше темпа' : pace === 'ahead' ? 'идёшь с запасом' : 'в темпе'}
            {pace === 'over' && (
              <span style={{ color: theme.colors.sub, fontStyle: 'italic' }}> — дневной лимит уже пересчитан мягче</span>
            )}
          </div>
          {(freedReserve > 0 || mb.oneOffSpent > 0) && (
            <div style={{ fontSize: 12, color: theme.colors.sub, fontStyle: 'italic', marginTop: 4 }}>
              {freedReserve > 0 && <>из конвертов вскрыто +{fmtK(freedReserve)}</>}
              {freedReserve > 0 && mb.oneOffSpent > 0 && ' · '}
              {mb.oneOffSpent > 0 && <>разовые {fmtK(mb.oneOffSpent)} вне темпа</>}
            </div>
          )}

          {/* 02 · Водопад */}
          <SectionHead n="02" title="Как распределяется доход" top />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 14 }}>
            <Band width={100} height={34} bg={theme.colors.inkTints[0]} fg={theme.colors.paper} label="доход" amount={fmtMoney(mb.expectedIncome, '')} />
            <div style={{ display: 'flex' }}>
              <div style={{ width: `${wf.fixedPct}%` }} />
              <div style={{ width: 1, background: theme.colors.sub, height: 10 }} />
            </div>
            <Band width={wf.fixedPct} height={28} bg={theme.colors.inkTints[2]} fg={theme.colors.paper} label="обязательное" amount={`−${fmtK(mb.fixedExpected)}`} />
            <div style={{ display: 'flex', alignItems: 'center', marginTop: 2 }}>
              <div style={{ width: `${wf.fixedPct}%` }} />
              <div
                style={{
                  height: 28,
                  width: `${Math.max(wf.goalPct, 6)}%`,
                  background: theme.colors.green,
                  color: theme.colors.paper,
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0 8px',
                  fontSize: 12,
                  whiteSpace: 'nowrap',
                  boxSizing: 'border-box',
                }}
              >
                цель
              </div>
              <span style={{ fontSize: 12, color: theme.colors.sub, marginLeft: 8, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                −{fmtK(mb.monthTarget)} · под защитой
                <button onClick={() => { setTargetStr(mb.monthTarget > 0 ? String(mb.monthTarget) : ''); setEditTarget(!editTarget); }} style={linkBtn}>
                  изменить
                </button>
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', marginTop: 2 }}>
              <div style={{ width: `${wf.fixedPct + wf.goalPct}%` }} />
              <Band width={wf.flexPct} height={34} bg={theme.colors.inkTints[3]} fg={theme.colors.ink} label="повседневное" flexChild />
            </div>
          </div>
          {editTarget && (
            <div style={{ margin: '10px 0 0', display: 'grid', gap: 6 }}>
              <div style={{ display: 'flex', gap: 6 }}>
                <input type="number" inputMode="decimal" value={targetStr} onChange={(e) => setTargetStr(e.target.value)} placeholder="20000" style={inputStyle} />
                <button disabled={busy} onClick={() => {
                  const n = Number(targetStr);
                  if (!Number.isFinite(n) || n < 0) { toast.show('число ≥ 0', 'error'); return; }
                  void saveTarget(n > 0 ? Math.round(n) : null);
                }} style={smallBtn}><Icon name="check" size={14} /></button>
                <button disabled={busy} onClick={() => setEditTarget(false)} style={smallGhostBtn}><Icon name="close" size={14} /></button>
              </div>
              {goalsMonthly > 0 && (
                <button disabled={busy} onClick={() => void saveTarget(goalsMonthly)} style={smallGhostBtn}>= взносы целей ({fmtK(goalsMonthly)})</button>
              )}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
            <span className="num" style={{ fontSize: 22, color: theme.colors.green }}>{fmtMoney(mb.availableFlexTotal, currency)}</span>
          </div>
          <div style={{ fontSize: 12, color: theme.colors.sub, fontStyle: 'italic', marginTop: 4, textAlign: 'right' }}>
            делится на остаток недели, не на дни месяца
          </div>

          {/* 03 · Конверты */}
          {(reserveRows.length > 0 || swept.size > 0) && (
            <>
              <SectionHead n="03" title="Конверты месяца" top right={`закреплено ${fmtK(heldReserve)} ₽`} />
              {reserveRows.map((r) => {
                const isFree = released.has(r.name);
                const picking = sweepCat === r.name;
                return isFree ? (
                  <div
                    key={r.name}
                    className="rise"
                    style={{
                      marginTop: 12,
                      border: `1px solid ${theme.colors.green}`,
                      borderTop: `1px dashed ${theme.colors.green}`,
                      borderRadius: theme.radius.sm,
                      padding: '12px 14px',
                      position: 'relative',
                    }}
                  >
                    <button
                      onClick={() => void putReserve({ month: monthKey, category: r.name, state: null })}
                      title="запечатать обратно"
                      style={{
                        position: 'absolute',
                        top: 10,
                        right: 12,
                        transform: 'rotate(-7deg)',
                        border: `1.5px solid ${theme.colors.green}`,
                        color: theme.colors.green,
                        fontSize: 10,
                        letterSpacing: '.16em',
                        textTransform: 'uppercase',
                        padding: '2px 7px',
                        background: 'transparent',
                        fontFamily: theme.font,
                        cursor: 'pointer',
                      }}
                    >
                      в пуле
                    </button>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <span style={{ fontSize: 15, color: theme.colors.green }}>
                        <Icon name={categoryIconName({ name: r.name })} size={14} inline /> {r.name}
                      </span>
                      <span className="num" style={{ fontSize: 18, color: theme.colors.green, marginRight: 64 }}>+{fmtK(r.reserve)}</span>
                    </div>
                    <div style={{ fontSize: 12, color: theme.colors.sub, fontStyle: 'italic', marginTop: 4 }}>
                      вскрыт — деньги вернулись в повседневное
                    </div>
                    {viewData?.categories.find((c) => c.name === r.name) && (
                      <>
                        <button
                          onClick={() => setPlanCat((prev) => (prev === r.name ? null : r.name))}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            borderBottom: `1px dashed ${theme.colors.subSoft}`,
                            padding: '0 1px 1px',
                            marginTop: 6,
                            fontSize: 12,
                            color: theme.colors.sub,
                            cursor: 'pointer',
                            fontFamily: theme.font,
                          }}
                        >
                          править план
                        </button>
                        {planCat === r.name && (
                          <div style={{ marginTop: 10 }}>
                            <CategoryPlanPad
                              category={viewData.categories.find((c) => c.name === r.name)!}
                              cats={viewData.categories}
                              onClose={() => setPlanCat(null)}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ) : (
                  <div
                    key={r.name}
                    style={{
                      marginTop: 12,
                      border: `1.5px solid ${theme.colors.ink}`,
                      borderRadius: theme.radius.sm,
                      padding: '12px 14px',
                      background:
                        `linear-gradient(to bottom right, transparent 49.6%, ${theme.colors.ink} 49.6%, ${theme.colors.ink} 50.4%, transparent 50.4%) top left/50% 22px no-repeat,` +
                        `linear-gradient(to bottom left, transparent 49.6%, ${theme.colors.ink} 49.6%, ${theme.colors.ink} 50.4%, transparent 50.4%) top right/50% 22px no-repeat`,
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 14 }}>
                      <span style={{ fontSize: 15 }}>
                        <Icon name={categoryIconName({ name: r.name })} size={14} inline /> {r.name}
                      </span>
                      <span className="num" style={{ fontSize: 18 }}>{fmtK(r.reserve)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6, gap: 8 }}>
                      <span style={{ fontSize: 12, color: theme.colors.sub, fontStyle: 'italic' }}>запечатан — ждёт повода</span>
                      <span style={{ display: 'flex', gap: 6 }}>
                        <button
                          onClick={() => void putReserve({ month: monthKey, category: r.name, state: 'released' })}
                          style={envBtn(theme.colors.ink)}
                        >
                          вскрыть в пул
                        </button>
                        {openGoals.length > 0 && (
                          <button onClick={() => setSweepCat(picking ? null : r.name)} style={envBtn(theme.colors.line, theme.colors.sub)}>
                            в цель
                          </button>
                        )}
                      </span>
                    </div>
                    {picking && (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
                        {openGoals.map((g) => (
                          <button key={g.name} disabled={busy} onClick={() => void sweep(r.name, r.reserve, g.name)} style={goalChip}>
                            {g.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              {[...swept].map((name) => (
                <div
                  key={name}
                  style={{
                    marginTop: 10,
                    border: `1px solid ${theme.colors.line}`,
                    borderRadius: theme.radius.sm,
                    padding: '12px 14px 12px 22px',
                    position: 'relative',
                    color: theme.colors.sub,
                  }}
                >
                  <div style={{ position: 'absolute', left: 8, top: 6, bottom: 6, width: 0, borderLeft: `2px dotted ${theme.colors.subSoft}` }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <span style={{ fontSize: 15, textDecoration: 'line-through', textDecorationColor: theme.colors.sub }}>
                      <Icon name={categoryIconName({ name })} size={14} inline /> {name}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, fontStyle: 'italic', marginTop: 4 }}>погашен — смещён в цель</div>
                </div>
              ))}
            </>
          )}

          {/* Разовые */}
          {bigDaily.length > 0 && (
            <div style={{ marginTop: 30 }}>
              <span className="caption">крупные повседневные — пометь разовое, чтобы не давило темп</span>
              {bigDaily.map((op) => {
                const on = oneOffIds.has(op.id);
                const d = op.date.slice(8, 10) + '.' + op.date.slice(5, 7);
                return (
                  <div key={op.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderTop: `1px solid ${theme.colors.line}` }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14 }}>{op.category}</div>
                      <div style={{ fontSize: 11, color: theme.colors.sub }}>{d} · {fmtMoney(op.amount, currency)}</div>
                    </div>
                    <button
                      onClick={() => void updateTx({ id: op.id, is_one_off: !on })}
                      style={chipBtn(on)}
                    >{on ? <><Icon name="check" size={11} inline /> разовое</> : 'разовое'}</button>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** Каталожный заголовок секции: номер Fraunces 12 + заголовок WONK:1 21. */
function SectionHead({ n, title, right, top }: { n: string; title: string; right?: string; top?: boolean }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 10,
        ...(top ? { marginTop: 26, borderTop: `1px solid ${theme.colors.line}`, paddingTop: 16 } : {}),
      }}
    >
      <span style={{ fontFamily: theme.fontDisplay, fontVariationSettings: "'opsz' 144, 'WONK' 0", fontSize: 12, color: theme.colors.sub }}>{n}</span>
      <span style={{ fontFamily: theme.fontDisplay, fontWeight: 420, fontVariationSettings: "'opsz' 144, 'WONK' 1", fontSize: 21 }}>{title}</span>
      {right && <span className="caption" style={{ marginLeft: 'auto', fontSize: 11 }}>{right}</span>}
    </div>
  );
}

/** Полоса водопада. */
function Band({ width, height, bg, fg, label, amount, flexChild }: { width: number; height: number; bg: string; fg: string; label: string; amount?: string; flexChild?: boolean }) {
  return (
    <div
      style={{
        height,
        width: flexChild ? `${width}%` : `${width}%`,
        background: bg,
        color: fg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: amount ? 'space-between' : 'flex-start',
        padding: '0 10px',
        fontSize: 13,
        boxSizing: 'border-box',
        minWidth: 90,
      }}
    >
      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
      {amount && <span className="num" style={{ fontSize: 15, whiteSpace: 'nowrap', marginLeft: 6 }}>{amount}</span>}
    </div>
  );
}

function plural(n: number, a: string, b: string, c: string): string {
  const m10 = n % 10, m100 = n % 100;
  if (m100 >= 11 && m100 <= 14) return c;
  if (m10 === 1) return a;
  if (m10 >= 2 && m10 <= 4) return b;
  return c;
}

/** Кнопка-действие конверта: uppercase, прямые углы (закон радиусов «Б»). */
function envBtn(border: string, color: string = theme.colors.ink): React.CSSProperties {
  return {
    fontSize: 11, letterSpacing: '.08em', textTransform: 'uppercase', padding: '5px 11px',
    background: 'transparent', border: `1px solid ${border}`, borderRadius: theme.radius.sm,
    fontFamily: theme.font, cursor: 'pointer', color,
  };
}

function chipBtn(on: boolean): React.CSSProperties {
  return {
    fontSize: 12, padding: '5px 12px', borderRadius: 999, cursor: 'pointer', fontFamily: theme.font,
    background: on ? theme.colors.ink : 'transparent', color: on ? theme.colors.bg : theme.colors.ink,
    border: `1px solid ${on ? theme.colors.ink : theme.colors.line}`,
  };
}
const goalChip: React.CSSProperties = {
  fontSize: 12, padding: '5px 12px', borderRadius: 999, cursor: 'pointer', fontFamily: theme.font,
  background: theme.colors.green, color: theme.colors.bg, border: `1px solid ${theme.colors.green}`,
};
const inputStyle: React.CSSProperties = {
  flex: 1, padding: '8px 10px', fontSize: 14, border: `1px solid ${theme.colors.line}`,
  borderRadius: theme.radius.sm, background: theme.colors.bg, fontFamily: theme.font, outline: 'none', boxSizing: 'border-box',
};
const smallBtn: React.CSSProperties = {
  padding: '8px 12px', background: theme.colors.ink, color: theme.colors.paper, border: 'none',
  borderRadius: theme.radius.sm, fontFamily: theme.font, fontSize: 13, cursor: 'pointer',
};
const smallGhostBtn: React.CSSProperties = {
  padding: '8px 10px', background: 'transparent', color: theme.colors.sub, border: `1px solid ${theme.colors.line}`,
  borderRadius: theme.radius.sm, fontFamily: theme.font, fontSize: 12, cursor: 'pointer',
};
const linkBtn: React.CSSProperties = {
  marginLeft: 8, padding: '2px 6px', background: 'transparent', color: theme.colors.sub, border: 'none',
  fontFamily: theme.font, fontSize: 12, cursor: 'pointer', textDecoration: 'underline',
};
